import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useFormState } from 'react-final-form'
import { PlaceholderCell } from 'components/extend'
import { formatPrice } from 'helpers/common'
import debounce from 'lodash/debounce'
import { useGetDeliveryCostMutation } from 'modules/deliveryInfo/api'
import { DeliveryCostRequest } from 'modules/deliveryInfo/types'
import { OrderDeliveryInfoCargoInfoRequest } from 'schema'

import { OrderYarcheCreateFormData } from '../pages/OrderYarcheCreatePage/types'

const DEBOUNCE_DELAY = 500

const OrderYarcheCreateSummaryTotalPrice: FC = () => {
  const [totalPrice, setTotalPrice] = useState<string | undefined>(undefined)
  const { values } = useFormState<OrderYarcheCreateFormData>()
  const { departurePoint, destinationPoint, cargo } = values

  const [getDeliveryCost, { isLoading, reset: resetDeliveryCost }] = useGetDeliveryCostMutation()

  const fetchDeliveryCost = useCallback(
    async (departureTerminalId: string, destinationTerminalId: string, sizeId: string) => {
      const data = {
        departure: {
          point: {
            isCourier: false,
            terminalId: departureTerminalId
          }
        },
        destination: {
          point: {
            isCourier: false,
            terminalId: destinationTerminalId
          }
        },
        cargoInfo: {
          type: OrderDeliveryInfoCargoInfoRequest.type.SIZE,
          size: {
            sizeId: String(sizeId)
          }
        }
      }

      const result = await getDeliveryCost(data as unknown as DeliveryCostRequest)

      if ('data' in result) {
        setTotalPrice(result.data?.totalPrice)
      }
    },
    [getDeliveryCost]
  )

  const debouncedFetchDeliveryCost = useMemo(() => debounce(fetchDeliveryCost, DEBOUNCE_DELAY), [fetchDeliveryCost])

  useEffect(() => {
    if (!departurePoint?.terminalId || !destinationPoint?.terminalId || !cargo?.sizeId) {
      resetDeliveryCost()
      setTotalPrice(undefined)

      return
    }

    debouncedFetchDeliveryCost(departurePoint.terminalId, destinationPoint.terminalId, cargo.sizeId)

    return () => {
      debouncedFetchDeliveryCost.cancel()
    }
  }, [cargo?.sizeId, departurePoint?.terminalId, destinationPoint?.terminalId, debouncedFetchDeliveryCost])

  return isLoading ? (
    <PlaceholderCell height={20} />
  ) : (
    <div className="flex gap-2 text-title-md">
      <div>Стоимость</div>
      <div className="ml-auto">{totalPrice ? `${formatPrice(totalPrice, true)}  ₽` : '—'}</div>
    </div>
  )
}

export default OrderYarcheCreateSummaryTotalPrice
